// Email Delivery console (platform superAdmin only).
//
// Answers "what is BidLight emailing, to whom, and why": recipients
// aggregated from the EmailLog audit (written at the nodemailer
// transport layer, utils/mailAudit.js on the backend), with per-flow
// attribution (notify:<type>, routes/invites.js, ...), links to the
// responsible issue, a Blocked checkbox per recipient (suppression
// list — also accepts whole @domains), and one-click cleanup of the
// synthetic test-fixture addresses (u-<ts>-<rand>@x.com) that were
// bouncing off Gmail.
//
// Backend: GET /admin/email/summary?days=, GET /admin/email/logs?to=,
// PUT /admin/email/suppress, POST /admin/email/cleanup-synthetic.
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect, Link } from "react-router-dom";
import {
  Alert, Button, Card, Checkbox, Empty, Popconfirm, Select, Skeleton,
  Table, Tag, Tooltip, message,
} from "antd";
import { url, tokenRef } from "api";

async function api(endpoint, options = {}) {
  const token = localStorage.getItem(tokenRef);
  const res = await fetch(`${url}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.err || `HTTP ${res.status}`);
  return json;
}

const STATUS_COLOR = { sent: "green", failed: "red", suppressed: "orange" };

export default function EmailDeliveryPage() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [recipients, setRecipients] = useState([]);
  const [suppressions, setSuppressions] = useState([]);
  const [logsByEmail, setLogsByEmail] = useState({});
  const [cleaning, setCleaning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api(`/admin/email/summary?days=${days}`);
      setRecipients(res.recipients || []);
      setSuppressions(res.suppressions || []);
    } catch (e) {
      message.error(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (userInfoRedux && !userInfoRedux.admin) return <Redirect to="/dashboard" />;

  const toggleBlock = async (email, blocked) => {
    try {
      await api(`/admin/email/suppress`, {
        method: "PUT",
        body: JSON.stringify({
          email,
          blocked,
          reason: blocked ? "Blocked from Email Delivery console" : "",
        }),
      });
      message.success(`${blocked ? "Blocked" : "Unblocked"} ${email}`);
      load();
    } catch (e) {
      message.error(e.message);
    }
  };

  const cleanupSynthetic = async () => {
    setCleaning(true);
    try {
      const res = await api(`/admin/email/cleanup-synthetic`, { method: "POST" });
      message.success(
        `Blocked ${res.blockedDomain} and purged ${res.purgedLogs} synthetic log rows`
      );
      load();
    } catch (e) {
      message.error(e.message);
    } finally {
      setCleaning(false);
    }
  };

  const expandLogs = async (expanded, row) => {
    if (!expanded || logsByEmail[row.email]) return;
    try {
      const res = await api(
        `/admin/email/logs?to=${encodeURIComponent(row.email)}&limit=50`
      );
      setLogsByEmail((prev) => ({ ...prev, [row.email]: res.logs || [] }));
    } catch (e) {
      message.error(e.message);
    }
  };

  const failedTotal = recipients.reduce((a, r) => a + r.failed, 0);
  const syntheticCount = recipients.filter((r) => r.synthetic).length;

  const columns = [
    {
      title: "Recipient",
      dataIndex: "email",
      key: "email",
      render: (email, row) => (
        <span style={{ wordBreak: "break-all" }}>
          {email}{" "}
          {row.synthetic && (
            <Tooltip title="Auto-generated test-fixture address — not a real inbox">
              <Tag color="volcano">synthetic</Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Sent",
      dataIndex: "sent",
      width: 70,
      sorter: (a, b) => a.sent - b.sent,
    },
    {
      title: "Failed",
      dataIndex: "failed",
      width: 76,
      sorter: (a, b) => a.failed - b.failed,
      render: (v) => (v ? <Tag color="red">{v}</Tag> : 0),
    },
    {
      title: "Suppressed",
      dataIndex: "suppressed",
      width: 100,
      render: (v) => (v ? <Tag color="orange">{v}</Tag> : 0),
    },
    {
      title: "Last activity",
      dataIndex: "lastAt",
      width: 130,
      sorter: (a, b) => new Date(a.lastAt) - new Date(b.lastAt),
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "Sending flow",
      dataIndex: "sources",
      render: (sources) => (
        <>
          {(sources || []).map((s) => (
            <Tag key={s} color={s.startsWith("notify:") ? "blue" : "default"}>
              {s}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Responsible issue",
      dataIndex: "issueIds",
      width: 150,
      render: (ids) =>
        ids && ids.length ? (
          ids.map((id) => (
            <Link key={id} to={`/issue-viewer/${id}`} style={{ marginRight: 8 }}>
              open issue
            </Link>
          ))
        ) : (
          "—"
        ),
    },
    {
      title: "Blocked",
      key: "blocked",
      width: 84,
      filters: [{ text: "Blocked only", value: true }],
      onFilter: (v, row) => row.blocked === v,
      render: (_, row) => (
        <Checkbox
          checked={row.blocked}
          onChange={(e) => toggleBlock(row.email, e.target.checked)}
        />
      ),
    },
  ];

  const logColumns = [
    {
      title: "When",
      dataIndex: "createdAt",
      width: 150,
      render: (v) => new Date(v).toLocaleString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (s) => <Tag color={STATUS_COLOR[s]}>{s}</Tag>,
    },
    { title: "Subject", dataIndex: "subject", ellipsis: true },
    { title: "Flow", dataIndex: "source", width: 190 },
    {
      title: "Issue / project",
      key: "ref",
      width: 170,
      render: (_, log) => (
        <>
          {log.issueId && (
            <Link to={`/issue-viewer/${log.issueId._id || log.issueId}`}>
              {log.issueId.displayId || "issue"}
            </Link>
          )}
          {log.projectId && (
            <span style={{ marginLeft: 6, color: "#888" }}>
              {log.projectId.title || ""}
            </span>
          )}
          {!log.issueId && !log.projectId && "—"}
        </>
      ),
    },
    {
      title: "Error",
      dataIndex: "error",
      ellipsis: true,
      render: (v) => v || "—",
    },
  ];

  const activeSuppressions = suppressions.filter((s) => s.active);

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <h4 style={{ margin: 0 }}>Email Delivery</h4>
        <Select
          value={days}
          onChange={setDays}
          style={{ width: 140 }}
          options={[
            { value: 7, label: "Last 7 days" },
            { value: 30, label: "Last 30 days" },
            { value: 90, label: "Last 90 days" },
          ]}
        />
        <Button onClick={load}>Refresh</Button>
        <Popconfirm
          title="Block @x.com and purge synthetic log rows?"
          description="Blanket-blocks the auto-generated test addresses and removes their audit entries."
          onConfirm={cleanupSynthetic}
        >
          <Button danger loading={cleaning}>
            Clean up synthetic addresses
          </Button>
        </Popconfirm>
      </div>

      {failedTotal > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${failedTotal} failed send(s) in the selected window${
            syntheticCount ? ` — ${syntheticCount} synthetic recipient(s)` : ""
          }`}
        />
      )}

      {loading ? (
        <Skeleton active />
      ) : recipients.length === 0 ? (
        <Empty description="No outbound email logged in this window. Rows appear as BidLight sends notifications, invites and digests." />
      ) : (
        <Table
          rowKey="email"
          dataSource={recipients}
          columns={columns}
          size="small"
          pagination={{ pageSize: 25, showSizeChanger: false }}
          expandable={{
            onExpand: expandLogs,
            expandedRowRender: (row) => (
              <Table
                rowKey="_id"
                dataSource={logsByEmail[row.email]}
                columns={logColumns}
                size="small"
                loading={!logsByEmail[row.email]}
                pagination={false}
              />
            ),
          }}
        />
      )}

      <Card
        title={`Blocked addresses & domains (${activeSuppressions.length})`}
        size="small"
        style={{ marginTop: 20 }}
      >
        {activeSuppressions.length === 0 ? (
          <Empty description="Nothing blocked. Tick a recipient's Blocked checkbox above, or use the synthetic cleanup." />
        ) : (
          <Table
            rowKey="email"
            dataSource={activeSuppressions}
            size="small"
            pagination={false}
            columns={[
              { title: "Address / domain", dataIndex: "email" },
              { title: "Reason", dataIndex: "reason", render: (v) => v || "—" },
              { title: "Blocked by", dataIndex: "createdBy", render: (v) => v || "—" },
              {
                title: "Since",
                dataIndex: "updatedAt",
                render: (v) => new Date(v).toLocaleDateString(),
              },
              {
                title: "Blocked",
                key: "toggle",
                width: 84,
                render: (_, row) => (
                  <Checkbox
                    checked
                    onChange={() => toggleBlock(row.email, false)}
                  />
                ),
              },
            ]}
          />
        )}
      </Card>
    </div>
  );
}
